// The custom fields for the chosen ticket form (docs/API.md §5.2, §1.9).
//
// Multiple forms are in use and each presents a different set of fields, so the
// sidebar is rendered from the form's `fields` array rather than hardcoded
// (SPEC.md §9). Two rules that are easy to get wrong:
//
//   * `is_required` here is the **per-form** value from `ticket_form_fields`, not
//     the definition's own flag — a serial number is mandatory on the hardware
//     form and optional elsewhere (migration 005).
//   * `ticket_custom_values.value` is one text column, but the API is typed:
//     numbers as JSON numbers, checkboxes as booleans, multiselects as arrays.
//     `encodeCustomFields` is the single place that conversion happens.
//
// UI state is kept as strings (and arrays for multiselect) because that is what
// inputs produce; conversion happens once, on submit.

// The conversion and the validity checks live in custom-field-values.js (no JSX, so node
// tests can import them); re-exported here so callers keep one import.
export { encodeCustomFields, missingRequiredFields, invalidNumberFields } from './custom-field-values.js';

export default function CustomFields({ fields, values, errors, onChange }) {
  if (!fields.length) return null;

  function set(key, value) {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="grid gap-3">
      {fields.map((field) => {
        const id = `cf-${field.key}`;
        const value = values[field.key];
        const error = errors?.[field.key];

        return (
          <div key={field.id ?? field.key}>
            {field.data_type === 'checkbox' ? (
              <label className="flex items-center gap-2 text-sm text-ink" htmlFor={id}>
                {/* A native checkbox is painted by the UA, so `text-brand-600` and
                    `focus:ring-brand-600` never did anything here. `accent-color` is
                    the property that actually tints the tick, and `color-scheme`
                    (set on :root by app.css) already flips the box itself, so this
                    needs no dark variant. Focus follows the `btn` convention. */}
                <input
                  id={id}
                  type="checkbox"
                  className="size-4 rounded accent-brand-600
                             focus-visible:outline-2 focus-visible:outline-offset-2
                             focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
                  checked={value === true}
                  onChange={(event) => set(field.key, event.target.checked)}
                />
                {field.label}
              </label>
            ) : (
              <>
                <label className="field-label" htmlFor={id}>
                  {field.label}
                  {field.is_required && <span className="text-red-600 dark:text-red-400"> *</span>}
                </label>
                {renderInput(field, id, value, set)}
              </>
            )}
            {error && (
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One control for one field, chosen by `data_type`.
 *
 * EXPORTED, and it now has a second caller: the admin console renders descriptor
 * fields through it (web/src/components/admin/DescriptorForm.jsx), mapping its own
 * `{ type, control }` vocabulary onto `data_type` in
 * web/src/pages/admin/descriptor-form.js. The adapter runs in that direction
 * because `data_type` values are rows in `custom_field_defs` on a live system and
 * cannot be renamed, while a descriptor is a literal in a source file. Keeping one
 * renderer means a fix to a focus ring or a dark-mode contrast lands on both
 * screens; a near-copy would have drifted on the first such fix.
 */
export function renderInput(field, id, value, set) {
  switch (field.data_type) {
    case 'textarea':
      return (
        <textarea
          id={id}
          // Callers may ask for a taller box; `custom_field_defs` rows never carry
          // `rows`, so the ticket screen keeps the 3 it has always had.
          rows={field.rows ?? 3}
          className="field-input"
          value={value ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      );
    case 'number':
      return (
        <input
          id={id}
          type="number"
          className="field-input"
          value={value ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      );
    case 'date':
      // type="date" produces the YYYY-MM-DD the API wants for date fields, with
      // no parsing and no timezone in play (docs/API.md §1.3).
      return (
        <input
          id={id}
          type="date"
          className="field-input"
          value={value ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      );
    case 'select':
      return (
        <select
          id={id}
          className="field-input"
          value={value ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        >
          <option value="">-</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    case 'multiselect':
      // Checkboxes rather than <select multiple>: multi-select needs ctrl-click to
      // be usable, and this screen has to work without the mouse.
      return (
        <fieldset id={id} className="mt-1 grid gap-1">
          {(field.options ?? []).map((option) => {
            const selected = Array.isArray(value) && value.includes(option.value);
            return (
              <label key={option.value} className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="size-4 rounded accent-brand-600
                             focus-visible:outline-2 focus-visible:outline-offset-2
                             focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
                  checked={selected}
                  onChange={(event) => {
                    const current = Array.isArray(value) ? value : [];
                    set(
                      field.key,
                      event.target.checked
                        ? [...current, option.value]
                        : current.filter((entry) => entry !== option.value),
                    );
                  }}
                />
                {option.label}
              </label>
            );
          })}
        </fieldset>
      );
    case 'boolean':
      // A single checkbox. No `is_required` handling, deliberately: the
      // `missingRequiredFields` rule above already records that a checkbox is
      // always answered, so "required" on one cannot mean "must be ticked"
      // without turning an optional flag into a consent gate.
      //
      // Added for the admin console's descriptor fields; no ticket custom field
      // uses it today (`custom_field_defs.data_type` has 'checkbox' for that).
      // The caller supplies the visible label — DescriptorForm puts it beside the
      // box rather than above it, which is what a lone checkbox needs.
      return (
        <input
          id={id}
          type="checkbox"
          className="size-4 rounded accent-brand-600
                     focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
          checked={value === true}
          onChange={(event) => set(field.key, event.target.checked)}
        />
      );
    default:
      return (
        <input
          id={id}
          type="text"
          className="field-input"
          value={value ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      );
  }
}
